import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  fract, floor, sin, cos, mix, smoothstep, pow, max, min, abs, length, step,
  normalize, negate, clamp, exp, sqrt, texture,
  positionLocal, cameraViewMatrix, cameraProjectionMatrix, cameraPosition
} from 'three/tsl';
import { SATURN_GRADIENT_HEIGHT, SATURN_GRADIENT_RGB } from './saturnGradientLUT';

// Scene constants for the "Spaceship in Saturn's Rings" map.
//
// Used by the sky shader, the rock material, the scene directional light
// setup in Renderer, and the Saturn-side fill light placement in ArenaView.
// One authoritative source — flipping a sign here moves Saturn or the sun
// in lockstep across every shader/light that reads from it.
//
// Units are world metres (ship ≈ 24 m for scale). Saturn is exaggerated —
// not realistic — so it dominates one side while leaving room for the ring
// to sweep past us.
//
// Ring plane is flat (axis = +Y) so that when the camera looks forward the
// ring horizon is horizontal across the view. `saturnCenter.y = 0` puts
// Saturn at eye level with the ship so it doesn't sit high in the sky.
// The ship sits exactly in the ring plane (inside the ring half-thickness)
// at ~17% of the way between inner and outer edges — close to the inner
// edge so most of the ring extends outward from us and curves away past
// Saturn.
export const SATURN_SCENE = {
  // Saturn's centre in world coordinates — placed directly east of the
  // ship (pure +X) so the blue-NPC spawn-facing direction points straight
  // at the planet and the ring-line shrink points read as symmetric
  // around its disc. Y is slightly below 0 so origin sits a few metres
  // above the ring plane (still well inside the ±22 m slab); the small
  // offset makes the plane-intersection math non-degenerate so every
  // view ray samples ring density at a direction-dependent radial.
  saturnCenter:  new THREE.Vector3(5012, -3, 0),      // distance ≈ 5012 m
  saturnRadius:  3500,                                // angular radius ~35°
  ringInner:     4300,   // 1.23 × Saturn radius (tight against the planet)
  ringOuter:     8500,   // 2.43 × Saturn radius
  ringHalfThick: 22,     // m — the band we physically swim in at full thickness
  // Ring plane normal = Saturn's spin axis. Set to straight +Y so the ring
  // is flat and reads as a horizontal horizon line from the ship. Small
  // non-zero X component would tilt the horizon sideways; Z would tip it
  // forward/back. Kept pure so "looking forward ⇒ horizontal ring" holds.
  ringAxis:      new THREE.Vector3(0, 1, 0).normalize(),
  // Direction TOWARD the sun (dot with surface normals for shading).
  // Roughly west of the ship (opposite Saturn) with a slight forward-and-
  // up tilt — this keeps Saturn's day side the one that faces us while
  // angling the terminator so it curves visibly across the disc rather
  // than sitting as a vertical line down the middle.
  sunDir:        new THREE.Vector3(-0.93, 0.15, -0.32).normalize(),
};

// Sky for the "Spaceship in Saturn's Rings" map.
//
// Composition:
//   * Deep-space base — near-black zenith, faint cool mid, stars + nebulae.
//   * Ring plane — real world-space ray-plane intersection. Alpha computed
//     from a chord-density model (optical-depth through the ring thickness),
//     so rays grazing the plane see a dense, saturated band and rays close
//     to perpendicular see thin ring material. This is what makes the scene
//     feel like we're *inside* the rings: the same ring that we're flying
//     through continues across the sky and wraps around Saturn.
//   * Saturn — real world-space ray-sphere. Shaded with latitude bands and
//     a day/night terminator lit by the shared `uSunDir`.
//   * Sun — a small bright disc + bloom in the sun direction, visible in
//     the far distance (left + forward).
//
// Vertex trick same as MoonSkyMaterial / SpaceMaterial: strip camera
// translation (vec4(pos, 0)) so the skybox follows the camera, then force
// clip-space z = w so after perspective divide the depth is 1 — the sky
// always renders behind everything else.
//
// Because we use `cameraPosition` in the fragment shader, ray origins are
// the *real* world camera position — which is what makes the ring parallax
// correctly as we orbit the ship (the ring near edge slides past us while
// the far edge sweeps past Saturn).
export class SaturnSkyMaterial extends NodeMaterial {
  readonly uTime = uniform(0);
  readonly uSaturnCenter  = uniform(SATURN_SCENE.saturnCenter.clone());
  readonly uSaturnRadius  = uniform(SATURN_SCENE.saturnRadius);
  readonly uRingInner     = uniform(SATURN_SCENE.ringInner);
  readonly uRingOuter     = uniform(SATURN_SCENE.ringOuter);
  readonly uRingHalfThick = uniform(SATURN_SCENE.ringHalfThick);
  readonly uRingAxis      = uniform(SATURN_SCENE.ringAxis.clone());
  // Saturn's spin axis = ring plane normal. Drives band orientation + ring
  // tilt; kept separate so art could decouple them later if desired.
  readonly uSaturnAxis    = uniform(SATURN_SCENE.ringAxis.clone());
  readonly uSunDir        = uniform(SATURN_SCENE.sunDir.clone());
  // Debug: scales Saturn's disc alpha. 1 = normal, 0 = completely
  // see-through (Saturn invisible; the ring + stars + nebulae behind it
  // render as if the planet weren't there). Driven from
  // `CONFIG.sim.debugSeeThroughSaturn` via ArenaView each frame.
  readonly uSaturnVisibility = uniform(1.0);
  // Orange-tint knob (0 = untouched baked gradient, 1 = fully replaced
  // by saturated orange). Default 0 so the shader faithfully reproduces
  // the PNG gradient; the debug panel binding lets the artist dial up
  // a warmer, more-saturated look without editing the source art.
  readonly uOrangeTint = uniform(0.0);
  // 1×1024 baked gradient LUT, built from references/saturn-gradient-1x1024.png
  // via scripts/sample-saturn-gradient.py. Sampled per-latitude with a
  // slow fbm wiggle so the bands read as "gas-cloud swirled stripes"
  // rather than sharp circles.
  private readonly gradientTex: THREE.DataTexture;

  constructor() {
    super();
    this.side = THREE.BackSide;
    this.depthWrite = false;
    this.depthFunc = THREE.LessEqualDepth;
    this.lights = false;

    // Build the RGBA DataTexture from the baked RGB bytes. We pad with
    // alpha=255 because THREE dropped RGBFormat in r140+.
    const rgba = new Uint8Array(SATURN_GRADIENT_HEIGHT * 4);
    for (let i = 0; i < SATURN_GRADIENT_HEIGHT; i++) {
      rgba[i * 4 + 0] = SATURN_GRADIENT_RGB[i * 3 + 0];
      rgba[i * 4 + 1] = SATURN_GRADIENT_RGB[i * 3 + 1];
      rgba[i * 4 + 2] = SATURN_GRADIENT_RGB[i * 3 + 2];
      rgba[i * 4 + 3] = 255;
    }
    this.gradientTex = new THREE.DataTexture(
      rgba,
      1,
      SATURN_GRADIENT_HEIGHT,
      THREE.RGBAFormat,
      THREE.UnsignedByteType,
    );
    this.gradientTex.magFilter = THREE.LinearFilter;
    this.gradientTex.minFilter = THREE.LinearFilter;
    this.gradientTex.wrapS = THREE.ClampToEdgeWrapping;
    this.gradientTex.wrapT = THREE.ClampToEdgeWrapping;
    this.gradientTex.colorSpace = THREE.SRGBColorSpace;
    this.gradientTex.needsUpdate = true;

    this.vertexNode = Fn(() => {
      const viewPos = cameraViewMatrix.mul(vec4(positionLocal, 0));
      const clip = cameraProjectionMatrix.mul(vec4(viewPos.xyz, 1));
      return vec4(clip.xy, clip.w, clip.w);
    })();

    // ------------------------------------------------------------------ utils

    const hash3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = vec3(
        p.dot(vec3(127.1, 311.7, 74.7)),
        p.dot(vec3(269.5, 183.3, 246.1)),
        p.dot(vec3(113.5, 271.9, 124.6))
      );
      return fract(sin(q).mul(43758.5453));
    });

    const noise3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const u = f.mul(f).mul(float(3).sub(f.mul(2)));

      const sample = (ox: number, oy: number, oz: number) =>
        hash3(i.add(vec3(ox, oy, oz))).x;

      const n000 = sample(0, 0, 0), n100 = sample(1, 0, 0);
      const n010 = sample(0, 1, 0), n110 = sample(1, 1, 0);
      const n001 = sample(0, 0, 1), n101 = sample(1, 0, 1);
      const n011 = sample(0, 1, 1), n111 = sample(1, 1, 1);

      const nx00 = mix(n000, n100, u.x);
      const nx10 = mix(n010, n110, u.x);
      const nx01 = mix(n001, n101, u.x);
      const nx11 = mix(n011, n111, u.x);
      const nxy0 = mix(nx00, nx10, u.y);
      const nxy1 = mix(nx01, nx11, u.y);
      return mix(nxy0, nxy1, u.z);
    });

    const fbm3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 5; i++) {
        sum.addAssign(noise3(q).mul(amp));
        q.assign(q.mul(2.03).add(vec3(13.1, 23.7, 7.2)));
        amp.assign(amp.mul(0.54));
      }
      return sum;
    });

    const starLayer = Fn((args: [Node<'vec3'>, Node<'float'>, Node<'float'>, Node<'float'>]) => {
      const [d, scale, threshold, sizeScale] = args;
      const p = d.mul(scale);
      const cell = floor(p);
      const f = fract(p);
      const h = hash3(cell);
      const centre = vec3(0.5).add(h.sub(0.5).mul(0.7));
      const dist = length(f.sub(centre));
      const mask = step(threshold, h.x);
      const twinkleA = sin(this.uTime.mul(2.3).add(h.y.mul(25.0))).mul(0.28);
      const twinkleB = sin(this.uTime.mul(5.1).add(h.z.mul(41.0))).mul(0.17);
      const twinkle = twinkleA.add(twinkleB).add(0.62);
      const size = h.z.mul(0.04).add(0.07).mul(sizeScale);
      const disk = pow(max(float(1).sub(dist.div(max(size, 0.0001))), 0), 4.5);
      return disk.mul(mask).mul(twinkle);
    });

    // ---------------------------------------------------------------- main

    this.colorNode = Fn(() => {
      // ----- feature flags (JS compile-time) -----
      // Flip to `true` to re-enable the procedural ring band. The full
      // ring pipeline (intersection, density, chord-alpha, colour,
      // shadow, etc.) is always built; this flag only decides whether
      // the result is composited into the final pixel. That way the
      // tuning work is preserved and can be brought back by changing a
      // single bool — no code deletion/restoration required.
      const RINGS_ENABLED = false;

      const d     = positionLocal.normalize().toVar();   // world-space view direction
      const cam   = cameraPosition;                      // world-space camera origin
      const C     = this.uSaturnCenter;
      const R     = this.uSaturnRadius;
      const ringN = normalize(this.uRingAxis).toVar();
      const satAxis = normalize(this.uSaturnAxis).toVar();
      const sunDir  = normalize(this.uSunDir).toVar();

      // =================================================================
      // Background: deep-space base + nebulae + stars — tinted warm to
      // read as "inside the ring". The base colours are pulled toward
      // Saturn-ring tan/amber rather than the cool blue-purple of open
      // deep space, so even when the explicit ring band is disabled the
      // sky still feels like we're embedded in dusty ring material.
      // =================================================================
      const h = d.y;
      const zenith = vec3(0.008, 0.006, 0.004);   // dark warm (ring-tinted)
      const midSky = vec3(0.022, 0.015, 0.008);   // mid warm
      const spaceBase = mix(midSky, zenith, smoothstep(0.2, 1.0, h));

      const nebA = fbm3(d.mul(1.9)).toVar();
      const nebB = fbm3(d.mul(3.4).add(vec3(11.0, 5.0, 23.0))).toVar();
      const nebC = fbm3(d.mul(1.3).add(vec3(47.0, 12.0, 91.0))).toVar();
      const nebShape = smoothstep(0.66, 0.90, nebA).mul(smoothstep(0.55, 0.85, nebB));
      const nebPurple = vec3(0.38, 0.12, 0.55);
      const nebBlue   = vec3(0.08, 0.22, 0.55);
      const nebTeal   = vec3(0.08, 0.35, 0.45);
      const nebHue = mix(
        mix(nebPurple, nebBlue, smoothstep(0.30, 0.55, nebC)),
        nebTeal,
        smoothstep(0.60, 0.85, nebC)
      );
      const nebula = nebHue.mul(nebShape.mul(0.30));

      // Slightly tightened top-layer threshold so the sun disc doesn't
      // compete with a bright starfield pixel sitting inside it.
      const stars1 = starLayer(d, 55,  0.992, 1.00);
      const stars2 = starLayer(d, 130, 0.982, 0.90);
      const stars3 = starLayer(d, 280, 0.994, 0.75);
      const starSum = stars1.mul(1.8).add(stars2.mul(1.1)).add(stars3.mul(0.7));
      const starCol = vec3(1.30, 1.20, 1.05).mul(starSum);

      const skyBase = spaceBase.add(nebula).add(starCol);

      // =================================================================
      // Saturn: ray-sphere intersection in world coordinates
      // =================================================================
      const oc = cam.sub(C).toVar();
      const b  = oc.dot(d).toVar();
      const cSphere = oc.dot(oc).sub(R.mul(R));
      const h2 = b.mul(b).sub(cSphere).toVar();
      const h2Pos = max(h2, float(0));
      const tSat = negate(b).sub(sqrt(h2Pos)).toVar();
      const hitSaturn = step(float(0), h2).mul(step(float(0.0001), tSat)).toVar();
      const Psat = cam.add(d.mul(tSat));
      const Nsat = normalize(Psat.sub(C)).toVar();

      // Non-reflective Saturn. Colour is pulled straight from the baked
      // 1×1024 gradient LUT (references/saturn-gradient-1x1024.png) —
      // no sun-direction shading, no terminator — so the planet reads
      // as a flat painted disc regardless of where the sun sits. This
      // is a deliberate choice; the reflective model was producing a
      // bright-side/dark-side split that wasn't wanted.
      //
      // Latitude picks the V coordinate; a very low-frequency fbm warp
      // adds a gas-cloud-like wiggle so the bands never read as
      // mathematically perfect circles. The wiggle's time drift is
      // ~0.01 rad/sec-equivalent, giving a barely-perceptible motion
      // over seconds and clearly visible deformation over minutes.
      const latSin = Nsat.dot(satAxis).toVar();

      const wiggleCoord = vec3(
        Nsat.x.mul(1.3).add(this.uTime.mul(0.008)),
        Nsat.y.mul(0.8),
        Nsat.z.mul(1.3).sub(this.uTime.mul(0.011)),
      );
      const wiggleField = fbm3(wiggleCoord).sub(0.5);
      // ±0.06 in V-space ≈ ±6% of the gradient's height. Enough to
      // noticeably deform bands, not so much that a pole sample can
      // drift all the way to the equator.
      const vWiggle = wiggleField.mul(0.06);

      // DataTexture defaults to flipY=false, so byte index 0 (which is
      // the TOP pixel of the PNG) sits at V=0. North pole (latSin=+1)
      // should map to the top of the gradient → V=0, south pole
      // (latSin=-1) → V=1. Hence the flip below.
      const vCoord = float(0.5).sub(latSin.mul(0.5)).add(vWiggle).toVar();
      const gradientSample = texture(this.gradientTex, vec2(float(0.5), vCoord)).xyz.toVar();

      // Orange tint. At 0 the sampled gradient passes through unchanged;
      // at 1 it's fully replaced with the saturated-orange target.
      // Mixing toward a pure-orange constant naturally boosts both hue
      // AND saturation (since the target is both shifted-warm and
      // zero-gray), which matches the "more orange and saturated" ask.
      const orangeTarget = vec3(1.0, 0.45, 0.08);
      const tintedSat   = mix(gradientSample, orangeTarget, this.uOrangeTint);

      // Dark-side shading. Rather than tying this to the real sunDir
      // (whose geometry puts Saturn's dark hemisphere entirely behind
      // the planet, invisible from the ship), aim a "dark axis" in the
      // ring plane directly. The axis is a unit vector in world XZ; a
      // surface point whose outward normal aligns with it is maximally
      // dark, opposite is maximally lit. This lets art direction place
      // the terminator on the visible disc without fighting sun
      // physics.
      //
      //   SATURN_DARK_YAW   — radians. Points the dark-side CENTER in
      //                         the ring plane:
      //                           0        → +X (far side of Saturn from
      //                                       ship — terminator hidden)
      //                           π/2≈1.57 → +Z (crescent on one limb)
      //                           π  ≈3.14 → −X (toward ship, whole
      //                                       visible disc in shadow)
      //                           3π/2     → −Z (crescent on the other
      //                                       limb)
      //                         Useful range: ~1.5 → ~2.5 for a visible
      //                         crescent. Start at 1.8.
      //   SATURN_NIGHT_FLOOR — brightness at the fully-dark antipode
      //                         (1 = no darkening, 0 = pitch black).
      //   SATURN_TERM_LO/HI — smoothstep bounds in Nsat·darkAxis space.
      //                         WIDER range = softer, broader gradient.
      //                         NARROWER = sharp, crisp terminator line.
      //                         With LO=-0.5, HI=0.5, about half the
      //                         sphere transitions; anywhere darker
      //                         than −0.5 is fully lit, anywhere
      //                         brighter than 0.5 is at the floor.
      const SATURN_DARK_YAW    = float(1.3);
      const SATURN_NIGHT_FLOOR = float(0.020);
      const SATURN_TERM_LO     = float(-0.55);
      const SATURN_TERM_HI     = float(0.45);

      // Dark axis in world XZ plane. At θ=0 it points +X; at π/2 +Z; at
      // π −X; at 3π/2 −Z.
      const cYs = cos(SATURN_DARK_YAW);
      const sYs = sin(SATURN_DARK_YAW);
      const darkDot = Nsat.x.mul(cYs).add(Nsat.z.mul(sYs));
      const darkT   = smoothstep(SATURN_TERM_LO, SATURN_TERM_HI, darkDot);
      const dayShade  = mix(float(1.0), SATURN_NIGHT_FLOOR, darkT);
      const saturnCol = tintedSat.mul(dayShade);

      // Anti-alias the limb — fade alpha over a small disc-tangent window.
      const limbAA = smoothstep(float(-0.0005), float(0.0005), h2);
      const saturnAlpha = hitSaturn.mul(limbAA).mul(this.uSaturnVisibility);

      // =================================================================
      // Ring plane intersection + chord-density alpha
      // =================================================================
      // Ray: cam + t*d hits plane through C normal ringN when t = (C-cam)·n / (d·n).
      const denom = d.dot(ringN).toVar();
      const numer = C.sub(cam).dot(ringN);
      // Guard against parallel rays. Very small epsilon — the chord-cap
      // below already clamps alpha for near-parallel rays, so we only need
      // the parallel flag to kill a razor-thin horizon seam where the
      // divisor would NaN.
      const parallel = step(abs(denom), float(0.00015));
      const tRing = numer.div(denom.add(parallel.mul(1.0))).toVar();
      const tRingValid = step(float(0.0001), tRing);

      const Pring = cam.add(d.mul(tRing));
      const rVec  = Pring.sub(C).toVar();
      // Remove any residual normal-component (numeric drift).
      const rInPlane = rVec.sub(ringN.mul(rVec.dot(ringN))).toVar();
      const rDist = length(rInPlane).toVar();

      const rNormRaw = rDist.sub(this.uRingInner)
                            .div(max(this.uRingOuter.sub(this.uRingInner), float(0.0001)));
      const rNorm = clamp(rNormRaw, 0, 1).toVar();
      const inAnnulus = step(this.uRingInner, rDist).mul(step(rDist, this.uRingOuter));

      // Radial density — broad A/B ring shape + Cassini + fine grooves.
      // Fine-band frequency ramps up near Saturn so the bands read as
      // classic Saturn striping close to the planet and as hazy asteroid
      // swirl out where we are.
      //
      // `satProximity` is 1 at the inner edge (rNorm=0, Saturn-facing) and
      // 0 by the time we reach our radial. It drives (a) sharpness of the
      // fine bands, (b) a darker baseline between bands (so stripes read
      // clearly), and (c) the ring half-thickness falloff below.
      const satProximity = smoothstep(float(0.17), float(0.00), rNorm).toVar();
      const fineFreq = mix(float(30.0), float(75.0), satProximity);
      const fineAmp  = mix(float(0.55), float(0.85), satProximity);
      const fineBase = mix(float(0.55), float(0.28), satProximity);
      const fine     = fbm3(vec3(rNorm.mul(fineFreq), float(7.3), float(0))).toVar();
      const fineTerm = fine.mul(fineAmp).add(fineBase);
      const broad    = smoothstep(0.00, 0.08, rNorm).mul(smoothstep(1.00, 0.85, rNorm));
      const cassini  = float(1).sub(
        smoothstep(0.46, 0.51, rNorm).mul(smoothstep(0.58, 0.51, rNorm))
      );
      const density  = broad.mul(cassini).mul(fineTerm).toVar();

      // Thickness is keyed on view direction's alignment with the Saturn
      // direction (projected into the ring plane). The thin axis is
      // therefore derived dynamically from `C - cam` so the shrink region
      // is always centred on Saturn — if Saturn moves in world space,
      // the symmetry is preserved automatically.
      //
      //   * Looking toward Saturn: THIN band — the ring passes behind the
      //     planet, far away, so it reads as a classic narrow striped
      //     line; Saturn's disc occludes most of it.
      //   * Looking perpendicular to Saturn in the ring plane (the ring
      //     passes closest to the ship here): THICK hazy band — the
      //     "we're swimming in the rings" zone.
      //   * Looking away from Saturn: still THICK — the far outer edge
      //     is not occluded and the alignment is clamped at zero so the
      //     opposite hemisphere never picks up the thin treatment.
      const saturnDirWorld  = normalize(C.sub(cam)).toVar();
      const thinAxisInPlane = normalize(saturnDirWorld.sub(ringN.mul(saturnDirWorld.dot(ringN)))).toVar();

      const dInPlaneVec  = d.sub(ringN.mul(d.dot(ringN)));
      const dInPlaneLen  = length(dInPlaneVec);
      const dInPlaneNorm = dInPlaneVec.div(max(dInPlaneLen, float(0.00005))).toVar();

      // max(cos θ, 0) → positive only on the Saturn-facing hemisphere, so
      // the opposite side of the ship never picks up any thin treatment.
      //
      // SINGLE TUNING KNOB → `thinFadeCenter`. It is cos(angle at which the
      // thin-to-thick fade sits at 50%). The ramp width is held constant
      // (±0.15 in cos space) so tweaking this number slides the whole fade
      // band inward or outward symmetrically — the ramp shape and angular
      // width stay the same, only its position moves.
      //
      //   thinFadeCenter reference values (θ = angle from Saturn axis):
      //     0.94 → θ≈20°   fade hidden deep inside Saturn's disc
      //     0.82 → θ≈35°   fade straddles Saturn's limb
      //     0.70 → θ≈46°   fade sits well outside Saturn, thin spills
      //                    into the surrounding sky before going thick
      //     0.55 → θ≈57°   thin region visually huge, stretches far past
      //                    the planet on both sides
      //
      // Lower = thin region LARGER / spills further from Saturn.
      // Higher = thin region TIGHTER / tucks back inside the disc.
      const alignSigned = max(dInPlaneNorm.dot(thinAxisInPlane), float(0));
      const thinFadeCenter = float(0.60);
      const alignFactor = smoothstep(
        thinFadeCenter.sub(float(0.15)),
        thinFadeCenter.add(float(0.15)),
        alignSigned,
      ).toVar();
      const thickAcross = float(1.0);    // perpendicular / away: full thickness
      const thinAlong   = float(0.16);   // toward Saturn: Saturn-ring line
      const thicknessFactor = mix(thickAcross, thinAlong, alignFactor).toVar();

      // Chord-through-ring optical depth.
      //   chord ≈ halfThick × thicknessFactor / |d·n|
      //   tau   = chord × density × absorption
      // denom≈0  (parallel)    → long chord, saturated ring.
      // denom≈1 (perpendicular)→ short chord, faint ring.
      //
      // Absorption is tuned so E/W (thicknessFactor=1) saturates the
      // horizon out to ~3° before starting to fall off — hazy band. N/S
      // (thicknessFactor=0.16) peaks at ~alpha 0.6 very close to horizon
      // and collapses within ~5° — thin banded line.
      const absorption = float(0.0020);
      const chordRaw = this.uRingHalfThick.mul(thicknessFactor)
        .div(max(abs(denom), float(0.0022)));
      const chord = min(chordRaw, float(3200)).toVar();
      const tau = chord.mul(density).mul(absorption);
      const ringAlphaRaw = float(1).sub(exp(negate(tau)));

      // Saturn occlusion: hide the portion of the ring that sits behind
      // the planet along this ray. Scaled by uSaturnVisibility so the
      // debug see-through toggle also reveals the ring arc behind Saturn.
      const ringBehindSaturn = hitSaturn.mul(step(tSat, tRing)).mul(this.uSaturnVisibility);

      const ringAlpha = ringAlphaRaw
        .mul(inAnnulus)
        .mul(tRingValid)
        .mul(float(1).sub(parallel))
        .mul(float(1).sub(ringBehindSaturn))
        .toVar();

      // Colour: warm gold in dense plates, cooler amber in thinner bands.
      const colWarm = vec3(0.96, 0.83, 0.55);
      const colCool = vec3(0.68, 0.55, 0.36);
      const ringBase = mix(colCool, colWarm, smoothstep(0.3, 0.9, density));

      // Saturn's shadow on the ring — darken the anti-sun side of the disc.
      // Simple proxy: the in-plane radial direction's dot with -sunDir.
      const rHat = normalize(rInPlane.add(ringN.mul(0.00001)));
      const shadowMask = smoothstep(0.20, 0.75, negate(rHat.dot(sunDir)))
        .mul(inAnnulus);
      const shadowTint = vec3(0.32, 0.27, 0.22);
      const ringShaded = mix(ringBase, shadowTint, shadowMask.mul(0.70));

      // Sun grazes the ring — brighter when the plane is lit edge-on-ish,
      // darker when the sun is near-perpendicular to the plane.
      const ringLit = max(abs(ringN.dot(sunDir)), float(0.18));
      const ringCol = ringShaded.mul(ringLit);

      // =================================================================
      // Sun disc + bloom
      // =================================================================
      // cos of half-angular-radius: 0.004 rad ≈ 0.23° (core), 0.010 rad
      // ≈ 0.57° (disc edge). The bloom used to be a `pow(cos, 280)`
      // exponential which decayed slowly and spread a big halo out to
      // ~10° — roughly 12× the disc's own radius, dominating the sky.
      //
      // Replaced with a tight smoothstep halo that starts exactly at
      // the disc edge (0.0100 rad) and fades out at 0.0105 rad, so the
      // total sun extent is ~1.05× the disc. Crank the outer angle up
      // (e.g. 0.013) if you want a bit more glow.
      const sunCos   = d.dot(sunDir).toVar();
      const sunDisc  = smoothstep(cos(float(0.010)),  cos(float(0.004)),  sunCos);
      const sunBloom = smoothstep(cos(float(0.0105)), cos(float(0.0100)), sunCos).mul(0.5);
      const sunContribution = vec3(1.25, 1.18, 0.95).mul(sunDisc.add(sunBloom));

      // =================================================================
      // Composite: sky → rings (behind Saturn suppressed) → Saturn → fog → sun
      // =================================================================
      const withRings  = RINGS_ENABLED ? mix(skyBase, ringCol, ringAlpha) : skyBase;
      const withSaturn = mix(withRings, saturnCol, saturnAlpha);

      // Ring-dust fog — banded by view direction. We're flying through
      // the thick of Saturn's ring, so the whole view picks up a warm
      // beige wash from the dust around us, BUT only for lines of sight
      // that run roughly along the ring plane. Rays pointing straight
      // up or straight down exit the slab almost immediately, so they
      // pass through less ring material and pick up less fog. Full fog
      // holds across the middle ~80% of the sphere (everything within
      // |d·ringN| < FOG_TAPER_START) and then tapers linearly to the
      // reduced floor amount by the time we reach straight up/down.
      //
      // Applied AFTER Saturn (the planet gets lightly tinted too —
      // atmosphere/haze between us and it) but BEFORE the sun (so the
      // sun punches through clean and stays the brightest thing in
      // view).
      //
      // Knobs:
      //   FOG_COLOR         — tint to push toward. Muted ring-palette
      //                         so it reads as ring-dust, not generic
      //                         brown fog.
      //   FOG_AMOUNT_BAND   — peak fog in the horizontal band (ring
      //                         plane directions).
      //   FOG_AMOUNT_CAPS   — reduced fog directly above and below.
      //                         Set equal to FOG_AMOUNT_BAND to disable
      //                         the outer banding (uniform fog).
      //   FOG_TAPER_START   — |d·ringN| at which the fog begins to
      //                         thin toward the caps.
      //   FOG_TAPER_END     — |d·ringN| at which fog hits the cap
      //                         floor. 1.00 = at straight up/down.
      //   FOG_AMOUNT_CORE   — extra-thick concentration along the ring
      //                         plane itself (|d·ringN| ≈ 0). Models the
      //                         fact that the ring is mostly a flat
      //                         sheet — sight-lines exactly in the plane
      //                         cut through the densest part of it.
      //   FOG_CORE_END      — |d·ringN| at which the core band has
      //                         fully dropped to FOG_AMOUNT_BAND. Keep
      //                         small (≤ 0.10) for a visually thin
      //                         slice of extra density around horizon.
      const FOG_COLOR       = vec3(0.40, 0.30, 0.18);
      const FOG_AMOUNT_CORE = float(0.90);
      const FOG_CORE_END    = float(0.06);
      const FOG_AMOUNT_BAND = float(0.60);
      const FOG_AMOUNT_CAPS = float(0.10);
      const FOG_TAPER_START = float(0.10);
      const FOG_TAPER_END   = float(0.70);

      const verticalDist = abs(d.dot(ringN));
      // Two smoothsteps layered: first lift the band into the CORE peak
      // for very small vertical distances, then taper from the band
      // down to the caps. Using the CORE value as the "inner" side of
      // the first mix and BAND as the "outer" side lets you tune the
      // three levels completely independently.
      const fogCoreFade = smoothstep(float(0), FOG_CORE_END, verticalDist);
      const fogMid      = mix(FOG_AMOUNT_CORE, FOG_AMOUNT_BAND, fogCoreFade);
      const fogTaper    = smoothstep(FOG_TAPER_START, FOG_TAPER_END, verticalDist);
      const fogAmount   = mix(fogMid, FOG_AMOUNT_CAPS, fogTaper).toVar();

      // Dust-grain texture for the fog. Two fbm layers stacked on the
      // view-direction:
      //   * a coarse layer (DUST_COARSE_SCALE) adds cloudy unevenness —
      //     large patches where the fog reads a little thicker and a
      //     little thinner, like drifting density streaks.
      //   * a fine layer (DUST_FINE_SCALE) adds the granular speckle
      //     that sells "millions of tiny rock fragments" — each feature
      //     is stable in world direction, so rotating the camera
      //     sweeps through them rather than scaling them like a
      //     post-process grain.
      // Both layers drift very slowly on perpendicular time axes so
      // the dust field subtly churns instead of shimmering.
      //
      //   DUST_MOD_STRENGTH     — 0 disables the texture (flat fog).
      //                           At ~1.0 the fog amount would swing
      //                           ±full value, reading as patchy fog.
      //                           0.25–0.45 gives "textured fog" that
      //                           still looks continuous.
      //   DUST_SPARKLE_STRENGTH — tiny additive highlight where both
      //                           layers align positively. Simulates
      //                           distant grains catching sunlight.
      //                           Keep very low; too much turns the
      //                           sky into stars.
      //   DUST_*_SPEED          — drift of each layer along its time
      //                           axis, in fbm feature-widths per
      //                           second. (The raw number is added to
      //                           one coord of the fbm domain per sec;
      //                           since features are ~1 unit wide in
      //                           that domain, the value reads directly
      //                           as feature-widths/s.) Keeping the two
      //                           layers on perpendicular axes and at
      //                           different rates avoids a unified
      //                           "everything sliding the same way"
      //                           look — it churns instead.
      const DUST_COARSE_SCALE     = float(550.0);
      const DUST_FINE_SCALE       = float(2400.0);
      const DUST_COARSE_SPEED     = float(0.35);
      const DUST_FINE_SPEED       = float(0.70);
      const DUST_MOD_STRENGTH     = float(0.2);
      const DUST_SPARKLE_STRENGTH = float(0.06);

      const dustCoarse = fbm3(
        d.mul(DUST_COARSE_SCALE).add(vec3(this.uTime.mul(DUST_COARSE_SPEED), float(0), float(0))),
      ).sub(0.5).toVar();
      const dustFine = fbm3(
        d.mul(DUST_FINE_SCALE).add(vec3(float(0), this.uTime.mul(negate(DUST_FINE_SPEED)), float(0))),
      ).sub(0.5).toVar();
      const dustField = dustCoarse.mul(0.55).add(dustFine.mul(0.9)).toVar();

      // Modulate fog density around 1.0 so the base amount is preserved
      // on average — the texture redistributes fog rather than adding
      // or subtracting net fog to the scene.
      const fogAmountTex = fogAmount.mul(float(1).add(dustField.mul(DUST_MOD_STRENGTH)));

      // Subtle brighter specks where both layers concentrate.
      const sparkleMask = smoothstep(float(0.25), float(0.55), dustCoarse.add(dustFine));
      const fogColorBase = FOG_COLOR.add(
        vec3(0.55, 0.43, 0.26).mul(sparkleMask).mul(DUST_SPARKLE_STRENGTH),
      );

      // Dark shadow patch in the fog. Starts from the anti-sun
      // direction (the part of the ring that's in Saturn's/our own
      // shadow), rotated around +Y by FOG_SHADOW_YAW so it can be
      // positioned independently of the literal sun geometry — e.g.
      // aimed squarely behind Saturn instead of exactly opposite the
      // sun. The darkening is a CONE, not a hemisphere: everything
      // outside the cone stays at full fog brightness, so the affected
      // region can be shrunk to just a small patch.
      //
      //   FOG_SHADOW_YAW    — rotation (radians) around +Y from
      //                         anti-sun. 0 = directly opposite the
      //                         sun. Positive values swing the shadow
      //                         centre east-and-around. With the current
      //                         sunDir, YAW ≈ 0.33 aligns the shadow
      //                         centre with +X (right behind Saturn).
      //   FOG_SHADOW_SIZE   — cos of the cone HALF-angle. Higher =
      //                         smaller dark patch. 0.55 ≈ 57° radius,
      //                         0.75 ≈ 41°, 0.90 ≈ 26°. Raise this to
      //                         shrink the affected area.
      //   FOG_SHADOW_FEATHER — cos-space edge width. Smoothstep runs
      //                         from (SIZE - FEATHER) → SIZE, so
      //                         larger FEATHER = softer cone edge.
      //   FOG_SHADOW_FLOOR  — brightness at the centre of the cone
      //                         (1 = no darkening, 0 = black fog).
      const FOG_SHADOW_YAW     = float(-0.4);
      const FOG_SHADOW_SIZE    = float(0.99);
      const FOG_SHADOW_FEATHER = float(0.15);
      const FOG_SHADOW_FLOOR   = float(0.5);

      // Rotate anti-sun around +Y by FOG_SHADOW_YAW. Right-hand Y-rot:
      // x' =  x·cos(θ) + z·sin(θ)
      // z' = -x·sin(θ) + z·cos(θ)
      const cY        = cos(FOG_SHADOW_YAW);
      const sY        = sin(FOG_SHADOW_YAW);
      const antiSun   = negate(sunDir);
      const shadowDir = vec3(
        antiSun.x.mul(cY).add(antiSun.z.mul(sY)),
        antiSun.y,
        antiSun.z.mul(cY).sub(antiSun.x.mul(sY)),
      );
      const shadowCos    = d.dot(shadowDir);
      const shadowAmount = smoothstep(
        FOG_SHADOW_SIZE.sub(FOG_SHADOW_FEATHER),
        FOG_SHADOW_SIZE,
        shadowCos,
      );
      const fogShade     = mix(float(1.0), FOG_SHADOW_FLOOR, shadowAmount);
      const fogColorTex  = fogColorBase.mul(fogShade);

      const fogged = mix(withSaturn, fogColorTex, fogAmountTex);

      const final       = fogged.add(sunContribution);

      return vec4(final, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
  /** Debug helper — set to 0 to render Saturn invisibly (ring + background
   *  behind the planet become visible through its silhouette). 1 = normal. */
  setSaturnVisibility(v: number): void { this.uSaturnVisibility.value = v; }
  /** Orange-tint knob. 0 = untouched baked gradient (default). 1 = fully
   *  replaced with saturated orange. Practical useful range ~0–0.4. */
  setOrangeTint(v: number): void { this.uOrangeTint.value = v; }
}

// Silence unused imports — kept for future tuning knobs.
void vec2; void min;
